import { AxiosError } from 'axios';
import { displayToast } from 'components/system/Toast';
import { FirebaseError } from 'firebase/app';
import { RecaptchaVerifier } from 'firebase/auth';
import { useFeedbackModal, useSession } from 'providers';
import { SESSION_STATE } from 'providers/SessionProvider/sessionProvider.constants';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { tooManyRequestsFeedbackModalProps } from 'utils/getAxiosTooManyRequestsFeedbackModalProps';
import { logError } from 'utils/logError';
import useYupValidationResolver from 'utils/useYupValidationResolver';
import * as yup from 'yup';
import { FORM_ERROR_MESSAGE } from 'yupHelper';
import { AUTH_STEP } from '../AuthManager/constants';
import { useAuthManagerContext } from '../AuthManager/Context/useAuthManagerContext';
import { useTwoFactorAuthFunctions } from './useTwoFactorAuthFunctions';

interface ICodeVerificationFields {
  code: string;
}

const codeRegex = new RegExp('^[0-9]{6}$');

const codeSchemaValidation = yup.object({
  code: yup.string().required(FORM_ERROR_MESSAGE.REQUIRED).matches(codeRegex, {
    message: FORM_ERROR_MESSAGE.INVALID_VERIFICATION_CODE,
  }),
});

const resendCodeSeconds = 60;

export const useCodeVerification = ({
  recaptchaVerifier,
  isNewPhone,
  onCompleted,
}: {
  recaptchaVerifier: RecaptchaVerifier | undefined;
  isNewPhone: boolean;
  onCompleted: () => void;
}) => {
  const [resendCodeSecondsLeft, setResendCodeSecondsLeft] =
    useState(resendCodeSeconds);
  const [loading, setLoading] = useState(false);
  const { setSessionState } = useSession();

  const { openFeedbackModal } = useFeedbackModal();
  const { setStep, phone } = useAuthManagerContext();
  const { sendVerificationCodeToUser, validateNewPhone, validatePhoneIdToken } =
    useTwoFactorAuthFunctions({
      recaptchaVerifier,
    });

  useEffect(() => {
    if (recaptchaVerifier) {
      void sendCode();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recaptchaVerifier]);

  useEffect(() => {
    if (resendCodeSecondsLeft <= 0) return;

    const timer = setInterval(() => {
      setResendCodeSecondsLeft((prevTime) => prevTime - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [resendCodeSecondsLeft]);

  const {
    control,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm<ICodeVerificationFields>({
    defaultValues: { code: '' },
    resolver: useYupValidationResolver(codeSchemaValidation),
  });

  const handleFormSubmit = useCallback(
    async (fields: ICodeVerificationFields) => {
      setLoading(true);

      const { code } = fields;

      try {
        if (!isNewPhone) {
          await validatePhoneIdToken(code);
        } else {
          if (!phone || !phone.phone || !phone.countryCallingCode) {
            throw new Error(
              'useCodeVerification does not have required phone data in AuthManagerContext',
            );
          }

          await validateNewPhone(code, phone.phone, phone.countryCallingCode);
        }

        setSessionState(SESSION_STATE.AUTHENTICATED);
        onCompleted();
      } catch (error: unknown) {
        if (
          error instanceof FirebaseError &&
          'auth/invalid-verification-code'
        ) {
          setError('code', { message: 'Invalid code. Please try again.' });
          return;
        }

        if (
          error instanceof AxiosError &&
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          (error.response?.data?.message as string)?.includes(
            'Session cookie not provided',
          )
        ) {
          displayToast({
            type: 'error',
            title: 'Session expired',
            description:
              'Your session has expired. Please enter your credentials again.',
          });
          setStep(AUTH_STEP.DEFAULT);
          return;
        }

        logError({ error });

        displayToast({
          type: 'error',
          title: 'Code verification failed',
          description: 'Something went wrong. Please try again later.',
        });
      } finally {
        setLoading(false);
      }
    },
    [
      isNewPhone,
      setSessionState,
      onCompleted,
      validatePhoneIdToken,
      phone,
      validateNewPhone,
      setError,
      setStep,
    ],
  );

  const handleBackToLogin = useCallback(() => {
    setStep(AUTH_STEP.DEFAULT);
  }, [setStep]);

  const resetResendTimer = () => {
    if (resendCodeSecondsLeft) {
      return;
    }

    setResendCodeSecondsLeft(resendCodeSeconds);
  };

  const sendCode = async () => {
    try {
      if (!phone?.phoneWithCallingCode) {
        throw new Error(
          'useCodeVerification does not have required phoneWithCallingCode data in AuthManagerContext',
        );
      }

      await sendVerificationCodeToUser(phone.phoneWithCallingCode);
      resetResendTimer();
    } catch (error: unknown) {
      logError({ error });

      if (
        error instanceof FirebaseError &&
        error.code === 'auth/too-many-requests'
      ) {
        resetResendTimer();
        openFeedbackModal(tooManyRequestsFeedbackModalProps);
        return;
      }

      displayToast({
        type: 'error',
        title: 'Failed to send code verification message',
        description: 'Something went wrong. Please try again',
      });
    }
  };

  return {
    handleBackToLogin,
    control,
    errors,
    sendCode,
    handleSubmit: handleSubmit(handleFormSubmit),
    loading,
    resendCodeSecondsLeft,
  };
};
